import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

import { gridSpacing } from 'store/constant';

import SliderFilter from './SliderFilter'
import GenderSplitSliderFilter from './GenderSplitSliderFilter';
import AutocompleteFilter from './AutocompleteFilter';
import { AdvFilterV2Context, FilterIcon } from '../filterContext';

import { getCityStateList } from 'api_handlers/discovery';
import { getPlannerAudienceInterests } from 'api_handlers/discovery';

import { updateFilters } from 'actions/planner-filters';
import { API_AGE_TO_DISPLAY, DEFAULT_BC_AGE_OPTIONS } from 'constants/bcAgeGroups';

const GENDER_SPLIT_INFO_TEXT =
  'Gender Split data is only available for a small subset of YCP creators — results may be limited while the YCP Roster toggle is on.';



const InfluencerFilters = ({ bcRosterOnly, bcUniqueOptions = {}, platform }) => {
  const dispatch = useDispatch();
  const v2 = useContext(AdvFilterV2Context);
  const audienceCredibility = useSelector((state) => state.plannerFilters.audienceCredibility);
  const ageGroup = useSelector((state) => state.plannerFilters.audienceAgeGroup);
  const audienceInterest = useSelector((state) => state.plannerFilters.audienceInterest);
  const audienceLocation = useSelector((state) => state.plannerFilters.audienceLocation);
  const plannerFilters = useSelector((state) => state.plannerFilters);

  const showBcFilters = platform === 'youtube' && bcRosterOnly === 'yes';

  const selectedWatchingInterests = Array.isArray(plannerFilters.watchingInterest) ? plannerFilters.watchingInterest : [];
  const selectedShoppingInterests = Array.isArray(plannerFilters.shoppingInterest) ? plannerFilters.shoppingInterest : [];
  const selectedAges = (plannerFilters.audienceAge || '').split(',').filter(Boolean);

  const watchingInterestsOptions = bcUniqueOptions.watchingInterests || [];
  const shoppingInterestsOptions = bcUniqueOptions.shoppingInterests || [];
  // Raw ES bucket keys (e.g. "age18To24") — display label comes from API_AGE_TO_DISPLAY.
  // Sort by the canonical order defined in DEFAULT_BC_AGE_OPTIONS so 18-24 always appears before 25-34.
  const rawAgeOptions = bcUniqueOptions.ageOptions || DEFAULT_BC_AGE_OPTIONS;
  const ageOptions = [...rawAgeOptions].sort(
    (a, b) => DEFAULT_BC_AGE_OPTIONS.indexOf(a) - DEFAULT_BC_AGE_OPTIONS.indexOf(b),
  );

  const [categoryOptions, setCategoryOptions] = React.useState([]);


  const [cityOptions, setCityOptions] = React.useState([]);

  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(async () => {

    const cityResponse = await getCityStateList('city');
    setCityOptions([...cityResponse.data.filter(option => option)]);

    const fieldOfInterest = await getPlannerAudienceInterests();
    setCategoryOptions([...fieldOfInterest]);
  
  }, [])

  return (
    <Grid container spacing={gridSpacing} sx={{height: 300, overflowY: 'scroll', pl: 4, pr: 2}}>
      <Grid item xs={12}>
       <Box className={v2 ? 'dv2-group-box' : undefined}>
        {v2 && <Typography className="dv2-group-label">Audience</Typography>}
        {!showBcFilters && (
          <SliderFilter value={audienceCredibility || 0} min={0} max={100} title='Credibility' payloadKey={'audienceCredibility'} showTextField={true} stepSize={0.1} />
        )}
        {!showBcFilters && (
          <Grid container spacing={2} alignItems='center' justifyContent='space-evenly' sx={{ mb: 2 }}>
            <Grid item xs={3}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                {v2 && <FilterIcon title="Age Group" />}
                Age Group
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Select
                labelId="ageGroup-select"
                id="ageGroup"
                name="ageGroup"
                value={ageGroup || (v2 ? '' : ageGroup)}
                displayEmpty={v2}
                renderValue={v2 ? (sel) => (sel ? sel : <span style={{ color: '#9aa1b2' }}>Select age group</span>) : undefined}
                onChange={(event) => {
                  dispatch(updateFilters('audienceAgeGroup', event.target.value))
                }}
                label="Age Group"
                fullWidth
                size='small'
              >
                <MenuItem value={'13-17'}>13 - 17</MenuItem>
                <MenuItem value={'18-24'}>18 - 24</MenuItem>
                <MenuItem value={'25-34'}>25 - 34</MenuItem>
                <MenuItem value={'35-44'}>35 - 44</MenuItem>
                <MenuItem value={'45-64'}>45 - 64</MenuItem>
                <MenuItem value={'65-'}>65 - </MenuItem>
              </Select>
            </Grid>
          </Grid>
        )}
        {!showBcFilters && (
          <AutocompleteFilter value={audienceInterest} options={categoryOptions} title='Interest' payloadKey={'audienceInterest'} />
        )}
        <AutocompleteFilter value={audienceLocation} options={cityOptions} title='Location' payloadKey={'audienceLocation'}/>
        <GenderSplitSliderFilter title='Gender Split' payloadKey={'audienceGenderSplit'} infoText={showBcFilters ? GENDER_SPLIT_INFO_TEXT : null} />
       </Box>
      </Grid>

      {/* BrandConnect Filters — visible only when YCP Roster toggle is on */}
      {showBcFilters && (
        <>
          <Grid item xs={12} sx={{ mt: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ padding: '2px 8px', background: '#dc2626', color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 4 }}>YCP</span>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>BrandConnect Filters</Typography>
              <Typography variant="caption" color="text.secondary">(Verified YouTube audience data)</Typography>
            </div>
          </Grid>

          {/* Watching Interest */}
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems='center' justifyContent='space-evenly' sx={{ mb: 1 }}>
              <Grid item xs={3}>
                <Typography variant="h6" gutterBottom>Watching Interest</Typography>
              </Grid>
              <Grid item xs={8}>
                <Autocomplete
                  multiple
                  size="small"
                  value={selectedWatchingInterests}
                  options={watchingInterestsOptions}
                  onChange={(event, newValue) => dispatch(updateFilters('watchingInterest', newValue || [], true))}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Interests" placeholder="Search interests..." />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Shopping Interest */}
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems='center' justifyContent='space-evenly' sx={{ mb: 1 }}>
              <Grid item xs={3}>
                <Typography variant="h6" gutterBottom>Shopping Interest</Typography>
              </Grid>
              <Grid item xs={8}>
                <Autocomplete
                  multiple
                  size="small"
                  value={selectedShoppingInterests}
                  options={shoppingInterestsOptions}
                  onChange={(event, newValue) => dispatch(updateFilters('shoppingInterest', newValue || [], true))}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Shopping Interests" placeholder="Search shopping interests..." />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Audience Age */}
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems='center' justifyContent='space-evenly' sx={{ mb: 1 }}>
              <Grid item xs={3}>
                <Typography variant="h6" gutterBottom>Audience Age</Typography>
              </Grid>
              <Grid item xs={8}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {ageOptions.map(age => (
                    <button
                      key={age}
                      onClick={() => {
                        const updated = selectedAges.includes(age)
                          ? selectedAges.filter(a => a !== age)
                          : [...selectedAges, age];
                        // Dispatch the raw ES bucket key (e.g. "age18To24") — the backend's
                        // `terms` filter against `audience_age_distribution_max` expects this
                        // exact format, not the human-readable label shown below.
                        dispatch(updateFilters('audienceAge', updated.join(','), true));
                      }}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 500,
                        border: selectedAges.includes(age) ? 'none' : '1px solid #e5e7eb',
                        cursor: 'pointer',
                        background: selectedAges.includes(age) ? '#dc2626' : '#fff',
                        color: selectedAges.includes(age) ? '#fff' : '#6b7280',
                        transition: 'all 0.15s'
                      }}
                    >
                      {API_AGE_TO_DISPLAY[age] || age}
                    </button>
                  ))}
                </div>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}

    </Grid>
  )
}

export default InfluencerFilters;